"use client";

import React, { type ReactElement, useEffect } from "react";
import {
  navigationAtom,
  sidebarVisibilityAtom,
  mapProjectionAtom,
  mapTileAtom,
} from "@/store";
import { useMapLibre } from "@/hooks";
import { X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import {
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  MapProjection,
  MapTile,
} from "@oreme/ui";
import { useTheme } from "next-themes";
import { setUserLocale } from "@/i18n/locale";
import type { Locale } from "@/i18n/config";
import { useAtom, useSetAtom } from "jotai";
import { mapProjections, mapTiles } from "@/utils";

function SharedSettingsSidebar() {
  const t = useTranslations("global.SettingsSidebar");
  const { map } = useMapLibre();
  const toggleSidebarVisibility = useSetAtom(sidebarVisibilityAtom);
  const setNavigation = useSetAtom(navigationAtom);
  const locale = useLocale();
  const [projection, setProjection] = useAtom(mapProjectionAtom);
  const [tile, setTile] = useAtom(mapTileAtom);
  const { theme, setTheme } = useTheme();

  const handleClose = () => {
    toggleSidebarVisibility();
    setNavigation(null);
    map!.resize();
  };

  const handleLocaleChange = async (value: string): Promise<void> => {
    await setUserLocale(value as Locale);
  };

  const handleThemeChange = (value: string): void => {
    setTheme(value);
  };

  const handleProjectionChange = (value: string): void => {
    setProjection(mapProjections.get(value) as MapProjection);
    if (map) map.setProjection({ type: value });
  };

  const handleTileChange = (value: string): void => {
    const newTile = mapTiles.get(value) as MapTile;
    setTile(newTile);
  };

  useEffect(() => {
    if (map) {
      map.on("style.load", () => {
        map.setProjection({
          type: projection.id,
        });
      });
    }
  }, [map, projection]);

  return (
    <div className="flex-1 flex flex-col w-full h-full">
      <header className="flex flex-row items-center justify-start pl-4 pr-2 h-12 rounded-none m-0 gap-3">
        <h3 className="text-base flex flex-row gap-3">
          <span>{t("title")}</span>
        </h3>
        <div className="inline-flex justify-center items-center ml-auto gap-1">
          <Button
            size="icon"
            onClick={() => {
              handleClose();
            }}
            variant="ghost"
            title={t("close")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </header>
      <div className="relative flex-1 overflow-hidden border-t">
        <section className="flex flex-col gap-2 p-4">
          <header>
            <h3 className="text-xs uppercase text-muted-foreground">
              {t("locale.title")}
            </h3>
          </header>
          <Select defaultValue={locale} onValueChange={handleLocaleChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("locale.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="system">System</SelectItem>
              <SelectItem value="fr">{t("locale.french")}</SelectItem>
              <SelectItem value="en">{t("locale.english")}</SelectItem>
            </SelectContent>
          </Select>
        </section>
        <section className="flex flex-col gap-2 p-4">
          <header>
            <h3 className="text-xs uppercase text-muted-foreground">
              {t("theme.title")}
            </h3>
          </header>
          <Select defaultValue={theme} onValueChange={handleThemeChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("theme.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="system">{t("theme.system")}</SelectItem>
              <SelectItem value="light">{t("theme.light")}</SelectItem>
              <SelectItem value="dark">{t("theme.dark")}</SelectItem>
            </SelectContent>
          </Select>
        </section>
        <section className="flex flex-col gap-2 p-4">
          <header>
            <h3 className="text-xs uppercase text-muted-foreground">
              {t("tile.title")}
            </h3>
          </header>
          <Select defaultValue={tile.id} onValueChange={handleTileChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("tile.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {Array.from(mapTiles).map(([key, tile]): ReactElement => {
                return (
                  <SelectItem key={key} value={tile.id}>
                    {tile.title}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </section>
        <section className="flex flex-col gap-2 p-4">
          <header>
            <h3 className="text-xs uppercase text-muted-foreground">
              {t("projection.title")}
            </h3>
          </header>
          <Select
            defaultValue={projection.id}
            onValueChange={handleProjectionChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("projection.placeholder")} />
            </SelectTrigger>
            <SelectContent>
              {Array.from(mapProjections).map(
                ([key, projection]): ReactElement => {
                  return (
                    <SelectItem key={key} value={projection.id}>
                      {t(`projection.${projection.id}`)}
                    </SelectItem>
                  );
                },
              )}
            </SelectContent>
          </Select>
        </section>
      </div>
    </div>
  );
}

export { SharedSettingsSidebar };
